// Builder de Email Sequence — admin agrega N steps con delay_hours acumulado
// implícito (cada step se calcula desde captured_at del subscriber).

import { useState } from 'react';

import { useCreateSequence } from '../hooks/useLeadMagnets';
import type { SequenceStepIn } from '../types';

interface Props {
  magnetId: number | null;
  onCreated?: () => void;
}

const EMPTY_STEP: SequenceStepIn = {
  order: 0,
  delay_hours: 0,
  subject: '',
  body_html: '',
};

export function SequenceBuilder({ magnetId, onCreated }: Props) {
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<SequenceStepIn[]>([{ ...EMPTY_STEP }]);
  const { submit, loading, error } = useCreateSequence();
  const [success, setSuccess] = useState<string | null>(null);

  if (magnetId == null) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        Selecciona un magnet para definir su email sequence.
      </div>
    );
  }

  const updateStep = (idx: number, patch: Partial<SequenceStepIn>) => {
    setSteps((cur) => cur.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addStep = () => {
    setSteps((cur) => [
      ...cur,
      {
        ...EMPTY_STEP,
        order: cur.length,
        delay_hours: cur.length === 0 ? 0 : 24,
      },
    ]);
  };

  const removeStep = (idx: number) => {
    setSteps((cur) =>
      cur.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })),
    );
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    if (!name.trim()) return;
    const res = await submit(magnetId, { name: name.trim(), active, steps });
    if (res) {
      setSuccess(`Sequence "${res.name}" creada con ${res.steps.length} steps.`);
      setSteps([{ ...EMPTY_STEP }]);
      setName('');
      onCreated?.();
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-3 p-4 border rounded-md max-w-3xl">
      <h3 className="text-base font-semibold">Email Sequence</h3>

      <div className="flex items-center gap-3">
        <label className="flex-1 block">
          <span className="text-xs text-muted-foreground">Nombre interno</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bienvenida personal-brand v1"
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Activa
        </label>
      </div>

      <div className="space-y-3">
        {steps.map((s, idx) => (
          <div key={idx} className="border rounded p-3 bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono">Step #{idx}</span>
              {steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeStep(idx)}
                  className="text-xs text-destructive underline"
                >
                  Eliminar
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-2">
              <label className="block">
                <span className="text-xs text-muted-foreground">delay_hours</span>
                <input
                  type="number"
                  min={0}
                  value={s.delay_hours}
                  onChange={(e) =>
                    updateStep(idx, { delay_hours: Number(e.target.value || 0) })
                  }
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                />
              </label>
              <label className="col-span-2 block">
                <span className="text-xs text-muted-foreground">Subject</span>
                <input
                  type="text"
                  required
                  value={s.subject}
                  onChange={(e) => updateStep(idx, { subject: e.target.value })}
                  className="mt-1 w-full border rounded px-2 py-1 text-sm"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-xs text-muted-foreground">Body HTML</span>
              <textarea
                required
                value={s.body_html}
                onChange={(e) => updateStep(idx, { body_html: e.target.value })}
                rows={4}
                placeholder="<p>Hola {{name}}, ...</p>"
                className="mt-1 w-full border rounded px-2 py-1 text-sm font-mono text-xs"
              />
            </label>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addStep}
        className="px-3 py-1 text-xs border rounded hover:bg-muted"
      >
        + Agregar step
      </button>

      {error && <div className="text-sm text-destructive">{error}</div>}
      {success && <div className="text-sm text-primary">{success}</div>}

      <button
        type="submit"
        disabled={loading}
        className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground disabled:opacity-50"
      >
        {loading ? 'Guardando…' : 'Guardar sequence'}
      </button>
    </form>
  );
}
